import React from 'react';
import { Link } from 'react-router-dom';
import { LogOut, User as UserIcon, Shield, Palette } from 'lucide-react';
import { User } from '../types';
import { useTheme } from '../context/ThemeContext';
import { Logo } from './Logo';

interface LayoutProps {
    children: React.ReactNode;
    user?: User;
    onLogout?: () => void;
    onEditProfile?: () => void;
}

export const Layout: React.FC<LayoutProps> = ({ children, user, onLogout, onEditProfile }) => {
    const { theme, toggleTheme } = useTheme();

    return (
        <div className="min-h-screen bg-surface-subtle flex flex-col font-sans text-main">
            <header className="bg-surface border-b border-border-base sticky top-0 z-40 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <Link to="/" className="hover:opacity-80 transition-opacity">
                        <Logo />
                    </Link>

                    {user && (
                        <div className="flex items-center gap-4">
                            {/* Theme Toggle */}
                            <button
                                onClick={toggleTheme}
                                className="flex items-center gap-2 text-xs font-medium px-2 py-1 rounded border border-border-base text-muted hover:text-main hover:bg-surface-subtle transition-colors uppercase tracking-wider"
                                title="Switch Theme"
                            >
                                <Palette className="w-3.5 h-3.5" />
                                {theme === 'legacy' ? 'Modern' : 'Rams'}
                            </button>

                            <div className="flex items-center gap-2 px-3 py-1.5 bg-surface-subtle rounded-full border border-border-base">
                                <div
                                    className="w-6 h-6 rounded-full border border-border-base"
                                    style={{ backgroundColor: user.color_preference }}
                                />
                                <span className="text-sm font-medium text-main">{user.full_name}</span>
                            </div>
                            <button
                                onClick={onEditProfile}
                                className="text-sm font-medium text-muted hover:text-main transition-colors"
                            >
                                Edit Profile
                            </button>
                            {(user.role === 'admin' || user.role === 'team_head') && (
                                <Link
                                    to="/admin"
                                    className="flex items-center gap-1 text-sm font-medium text-primary hover:text-primary-hover transition-colors"
                                >
                                    <Shield className="w-4 h-4" />
                                    Admin
                                </Link>
                            )}
                            <div className="h-6 w-px bg-border-base"></div>
                            <button
                                onClick={onLogout}
                                className="p-2 text-muted hover:text-main transition-colors"
                                title="Logout"
                            >
                                <LogOut className="w-5 h-5" />
                            </button>
                        </div>
                    )}
                </div>
            </header>

            <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {children}
            </main>
        </div>
    );
};
